/* eslint-disable max-lines */
import { Button, Card, CardHeader, CardBody, Container, Row, Col, Badge, Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { toast } from 'react-toastify';
import { useContext, useState, useEffect } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import { Link } from 'react-router-dom';
import SupportService from '../../../services/Support';
import { useParams } from 'react-router-dom';
import SupportListInterface, {
  SupportAttachment,
  validStatus,
  statusEnum,
} from '../../../interfaces/SupportListInterface';
import SupportReplyList from './SupportReplyList';
import SupportReplyNew from './SupportReplyNew';
import history from '../../../history';
import getFormatByMimetypes from '../../../helpers/getFormatByMimetypes';
import SupportAttachments from './SupportAttachments';
import SupportAttachmentsMobile from './SupportAttachmentsMobile';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import InlineSelect, { InlineSelectOption } from './InlineSelect';
import { AvatarComponent } from '../../../helpers/generateAvatarRamdomColor';
import RoutingSupportTickets from '../../../services/RoutingSupportTickets';
import AddUserOnDepartmentModal from './AddUserOnDepartmentModal';
import FormattedSupportText from './FormattedSupportText';
import isAdminHelper from '../../../helpers/isAdmin';
import isUpperManagement from '../../../helpers/isUpperManagement';
import isDirector from '../../../helpers/isDirector';
import isEducationDirectorAdminAssistant from '../../../helpers/isEducationDirectorAdminAssistant';
import isFinanceUser from '../../../helpers/isFinanceUser';
import isHRUser from '../../../helpers/isHRUser';
import { SUPPORT_DEPARTMENTS } from './taxonomies/departments';
import LoaderSpinner from '../../../components/Core/LoaderSpinner';
import { useFranchiseeTrelloIntegration } from '../../../hooks/useFranchiseeTrelloIntegration';

const SupportView = () => {
  const { token, currentSchool, user }: any = useContext(AuthContext);
  const { isTrelloActive, integrationChecked } = useFranchiseeTrelloIntegration();
  const [data, setData] = useState({} as SupportListInterface);
  const { id } = useParams<{ id: string }>();
  const [validStatuss, setValidStatuss] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [departmentUsers, setDepartmentUsers] = useState<any[]>([]);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const isAdmin = (user: any) => {
    return (
      isAdminHelper(user) ||
      isHRUser(user) ||
      isUpperManagement(user) ||
      isDirector(user) ||
      isEducationDirectorAdminAssistant(user) ||
      isFinanceUser(user)
    );
  };

  const handleRequestCloseAddUserModal = () => {
    setIsAddUserModalOpen(false);
  };

  const handleFindRecipients = async (department: string, ticketId: string) => {
    const result = await RoutingSupportTickets(token).list(currentSchool.value);

    const dataResult = result.data.filter(
      (value: any) => value.department === department || value.supportTicketId === ticketId,
    );

    setDepartmentUsers(dataResult);
  };

  const getSupportById = async () => {
    try {
      const result = await SupportService(token).show(id);
      const dataResult: SupportListInterface = result.data;
      setData(dataResult);
      if (dataResult.status) {
        setValidStatuss(validStatus[dataResult.status as statusEnum].color);
      }
      if (dataResult.department) {
        handleFindRecipients(dataResult.department, id);
      }
    } catch (e: any) {
      toast.error('Error: ' + id);
    } finally {
      setTicketLoading(false);
    }
  };

  const updateStatus = async (status: string) => {
    changeStatus(status);
  };

  const updatePriority = async (priority: string) => {
    changePriority(priority);
  };

  const handlePrioritySelectChange = (value: string) => {
    if (value !== data.priority) {
      updatePriority(value);
    }
  };

  const handleStatusSelectChange = (value: string) => {
    if (value !== data.status) {
      updateStatus(value);
    }
  };

  const changePriority = async (priority: string) => {
    if (loading) return;

    setLoading(true);

    const body = {
      status: data.status,
      priority,
    };
    try {
      await SupportService(token).update(id, body);
      toast.success(`Priority updated with successfully!`);
      const newData = data;
      newData.priority = priority;
      setData(newData);
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const changeStatus = async (status: string) => {
    if (loading) return;

    setLoading(true);

    const body = {
      status,
    };
    try {
      const result = await SupportService(token).update(id, body);
      toast.success(`Status updated with successfully!`);
      const newData = data;
      newData.status = status;
      setData(newData);
      if (result?.data?.id) {
        history.push(`/support/${result?.data?.id}`);
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'error!');
      toast.error(message);
    }

    setLoading(false);
  };

  const handleReplyCreated = () => {
    setRefresh(!refresh);
  };

  const ucfirst = (text: string) => {
    if (text === '' || text === undefined) {
      return '';
    }
    return text[0].toUpperCase() + text.substring(1).toLowerCase();
  };

  const isStatusSegmentDisabled = (s: string) => {
    if (data.status === 'PENDING' && s === 'OPEN') return true;
    if (data.status === 'CLOSED' && (s === 'OPEN' || s === 'PENDING')) return true;
    return false;
  };

  useEffect(() => {
    getSupportById();
  }, []);

  useEffect(() => {
    handleFindRecipients(data.department, data.id);
  }, [isAddUserModalOpen]);

  const supportAttachments: SupportAttachment[] = data.attachments || [];
  const { isMobile } = useCheckMobileScreen();

  const isPdfFileAnswer = (answer: string | undefined) => {
    const t = (answer || '').trim();
    if (!t) return false;
    const base = t.split('?')[0].split('#')[0];
    return base.toLowerCase().endsWith('.pdf');
  };

  const questionAnswersForDisplay = (data.questionAnswers || []).filter(
    (qa) => qa.answer !== '' && !isPdfFileAnswer(qa.answer),
  );

  const priorityBadgeColor = (p: string) => {
    if (p === 'HIGH') return 'danger';
    if (p === 'MEDIUM') return 'warning';
    return 'primary';
  };

  const priorityOptions: InlineSelectOption[] = [
    { value: 'LOW', label: ucfirst('LOW'), color: '#3b82c4' },
    { value: 'MEDIUM', label: ucfirst('MEDIUM'), color: '#f0a02a' },
    { value: 'HIGH', label: ucfirst('HIGH'), color: '#e85d75' },
  ];

  const statusOptions: InlineSelectOption[] = [
    {
      value: 'OPEN',
      label: ucfirst(statusEnum.OPEN),
      color: '#1d9d6f',
      disabled: isStatusSegmentDisabled('OPEN'),
    },
    {
      value: 'PENDING',
      label: ucfirst(statusEnum.PENDING),
      color: '#f0a02a',
      disabled: isStatusSegmentDisabled('PENDING'),
    },
    {
      value: 'CLOSED',
      label: ucfirst(statusEnum.CLOSED),
      color: '#8b94a3',
      disabled: isStatusSegmentDisabled('CLOSED'),
    },
  ];

  const departmentLabel = SUPPORT_DEPARTMENTS.find((d) => d.id === data.department)?.label ?? data.department ?? '';

  const shortTicketDate =
    data.createdAt &&
    new Date(data.createdAt as unknown as string).toLocaleDateString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    });

  const centerLabel = data.school?.name ?? '';

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col className="order-xl-2" xl="12">
            <Card className="shadow-sm border bg-white mb-4">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="6">
                    <h3 className="mb-0">Support Ticket</h3>
                    <div className="support-ticket-breadcrumb">
                      <Breadcrumb className="p-0 m-0 mb-1">
                        <BreadcrumbItem>
                          <Link to="/support/">Support Tickets</Link>
                        </BreadcrumbItem>
                        <BreadcrumbItem active>Ticket #{id}</BreadcrumbItem>
                      </Breadcrumb>
                    </div>
                  </Col>
                  <Col className="text-right" xs="6">
                    {isAdmin(user) && integrationChecked && !isTrelloActive && (
                      <Button className="mr-2" onClick={() => setIsAddUserModalOpen(true)}>
                        <i className="fas fa-cog" />
                      </Button>
                    )}
                    <Link to="/support/">
                      <Button color="primary">Back</Button>
                    </Link>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="pt-0 p-4 p-lg-5">
                {ticketLoading ? (
                  <Row>
                    <Col className="position-relative" style={{ minHeight: '280px' }}>
                      <LoaderSpinner />
                    </Col>
                  </Row>
                ) : (
                  <div className="d-flex align-items-start">
                    <div className="flex-shrink-0 d-none d-md-block">
                      <AvatarComponent
                        name={`${data?.user?.firstName || ''} ${data?.user?.lastName || ''}`.trim()}
                        size="56"
                        userPicture={data?.user?.picture}
                      />
                    </div>
                    <div className="flex-fill pl-0 pl-md-4" style={{ minWidth: 0 }}>
                      <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
                        <div className="pr-2 mb-2 mb-md-0">
                          <h3 className="mb-0 font-weight-bold text-dark">
                            {data?.user?.firstName || ''} {data?.user?.lastName || ''}
                          </h3>
                          <p className="text-muted small mb-0">{data?.user?.email}</p>
                          <div className="d-flex flex-wrap align-items-center w-100">
                            {departmentUsers.map((row: any) => (
                              <div key={row.id} className="mr-2 mb-2">
                                <AvatarComponent
                                  name={`${row.user.firstName} ${row.user.lastName}`}
                                  size="32"
                                  userPicture={row.user.picture}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="d-flex flex-wrap align-items-center">
                          {isAdmin(user) ? (
                            <div className="mr-3 mb-2">
                              <InlineSelect
                                label="Priority"
                                iconType="priority"
                                ariaLabel="Change priority"
                                value={data.priority || 'LOW'}
                                onChange={handlePrioritySelectChange}
                                disabled={loading}
                                options={priorityOptions}
                              />
                            </div>
                          ) : (
                            <>
                              <Badge color={priorityBadgeColor(data.priority)} className="font-weight-bold mr-2 mb-2">
                                {ucfirst(data.priority)}
                              </Badge>
                            </>
                          )}
                          {isAdminHelper(user) ? (
                            <div className="mb-2">
                              <InlineSelect
                                label="Status"
                                iconType="dot"
                                ariaLabel="Change status"
                                value={data.status || 'OPEN'}
                                onChange={handleStatusSelectChange}
                                disabled={loading}
                                options={statusOptions}
                              />
                            </div>
                          ) : (
                            <Badge color={validStatuss || 'secondary'} className="font-weight-bold mb-2">
                              {data.status}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="d-flex flex-wrap align-items-center mb-3 small">
                        <div className="d-flex align-items-center mr-4 mb-1">
                          <span className="font-weight-bold text-muted mr-1">Date:</span>
                          <span className="text-dark">{shortTicketDate || '—'}</span>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-1">
                          <span className="font-weight-bold text-muted mr-1">Center:</span>
                          <span className="text-dark">{centerLabel}</span>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-1">
                          <span className="font-weight-bold text-muted mr-1">Department:</span>
                          <span className="text-dark">{departmentLabel || '—'}</span>
                        </div>
                        <div className="d-flex align-items-center mr-4 mb-1">
                          <span className="font-weight-bold text-muted mr-1">Category:</span>
                          <span className="text-dark">{ucfirst(data.type)}</span>
                        </div>
                        <div className="d-flex align-items-center mb-1">
                          <span className="font-weight-bold text-muted mr-1">Subcategory:</span>
                          <span className="text-dark">{data.subcategory || '—'}</span>
                        </div>
                      </div>

                      <hr className="my-3" />

                      {data?.questionAnswers &&
                        data.questionAnswers.length > 0 &&
                        (questionAnswersForDisplay.length > 0 || supportAttachments.length > 0) && (
                          <div className="mb-4">
                            {questionAnswersForDisplay.length > 0 && (
                              <p className="font-weight-bold mb-3 text-uppercase small text-muted">
                                Questions & answers
                              </p>
                            )}
                            <div className="border rounded p-3 bg-white">
                              {questionAnswersForDisplay.map((qa, idx) => (
                                <div key={idx} className="mb-3">
                                  <p className="text-primary text-uppercase small font-weight-bold mb-2">
                                    {qa.question}
                                  </p>
                                  <div>
                                    <FormattedSupportText text={qa.answer} />
                                  </div>
                                </div>
                              ))}
                              {supportAttachments.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-primary text-uppercase small font-weight-bold mb-2">
                                    Attachments
                                    <span className="text-muted font-weight-normal ml-2 text-lowercase">
                                      {supportAttachments.length} {supportAttachments.length === 1 ? 'file' : 'files'}
                                    </span>
                                  </p>
                                  {isMobile ? (
                                    <SupportAttachmentsMobile
                                      attachments={supportAttachments}
                                      resolveName={(attachment) =>
                                        attachment.name?.split('/').pop() ||
                                        `${data.id}.${getFormatByMimetypes(attachment.mimetype || '')}`
                                      }
                                    />
                                  ) : (
                                    <SupportAttachments
                                      attachments={supportAttachments}
                                      resolveName={(attachment) =>
                                        attachment.name?.split('/').pop() ||
                                        `${data.id}.${getFormatByMimetypes(attachment.mimetype || '')}`
                                      }
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {!ticketLoading && (
              <Card className="shadow-sm border bg-white">
                <CardBody className="p-4 p-lg-5">
                  <h3 className="mb-4 font-weight-bold">Conversation</h3>
                  <SupportReplyList refresh={refresh} ticketUserId={data.user?.id} />
                  <SupportReplyNew onCreateReply={handleReplyCreated} schoolId={data.schoolId} />
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
      <AddUserOnDepartmentModal
        isOpen={isAddUserModalOpen}
        OnRequestedClose={handleRequestCloseAddUserModal}
        department={data.department}
        ticketId={data.id}
      />
    </>
  );
};

export default SupportView;
